import React, { useEffect, useState } from "react";
import axiosInstance from "./api/axiosInstance";
import PasswordInput from "./PasswordInput";
import {
  ACCESS_TYPES,
  ACCOUNT_OWNERS,
  LOGIN_METHODS,
  LOGIN_OWN,
  LOGIN_PROVIDER_PLATFORMS,
  PLATFORMS_WITH_PAGE_URL,
  SOCIAL_ACCOUNT_STATUSES,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORM_VALUES,
  TWO_FA_METHODS,
  VERIFICATION_STATUSES,
  socialPlatformLabel,
} from "../constants/socialPlatforms";
import { mobileValidationMessage } from "../utils/phone";

export function emptySocialAccountForm(clientId = "") {
  return {
    platformName: "",
    accountName: "",
    loginMethod: LOGIN_OWN,
    linkedAccountId: "",
    username: "",
    password: "",
    profileUrl: "",
    businessUrl: "",
    emailUsed: "",
    recoveryEmail: "",
    contactNumber: "",
    twoFaEnabled: "",
    twoFaMethod: "",
    twoFaHolder: "",
    accessType: "",
    accountOwner: "",
    verificationStatus: "",
    clientId: clientId ? String(clientId) : "",
    status: "Active",
    notes: "",
  };
}

export function socialAccountToForm(account) {
  const rawPlatform = account.Platform_Name || "";
  const lower = rawPlatform.toLowerCase();
  const twoFa = account.Two_FA_Enabled;
  return {
    ...emptySocialAccountForm(account.client_id ?? ""),
    platformName: SOCIAL_PLATFORM_VALUES.includes(lower) ? lower : rawPlatform,
    accountName: account.Account_Name || "",
    loginMethod: account.Login_Method || LOGIN_OWN,
    linkedAccountId: account.Linked_Account_Id ? String(account.Linked_Account_Id) : "",
    username: account.Username || "",
    profileUrl: account.Profile_URL || "",
    businessUrl: account.Business_URL || "",
    emailUsed: account.Email_Used || "",
    recoveryEmail: account.Recovery_Email || "",
    contactNumber: account.Contact_Number || "",
    twoFaEnabled: twoFa === true ? "yes" : twoFa === false ? "no" : "",
    twoFaMethod: account.Two_FA_Method || "",
    twoFaHolder: account.Two_FA_Holder || "",
    accessType: account.Access_Type || "",
    accountOwner: account.Account_Owner || "",
    verificationStatus: account.Verification_Status || "",
    status: account.Status || "Active",
    notes: account.Notes || "",
  };
}

const isOwnLogin = (form) => (form.loginMethod || LOGIN_OWN) === LOGIN_OWN;

/** Message for the first missing / invalid field, or "" when the form can be saved. */
export function socialAccountFormError(form, { hasSavedPassword = false } = {}) {
  if (!form.platformName || !form.accountName.trim()) return "Platform and Account Name are required.";
  if (isOwnLogin(form) && (!form.username.trim() || (!form.password.trim() && !hasSavedPassword))) {
    return "Username and Password are required when the account has its own login.";
  }
  return mobileValidationMessage(form.contactNumber, { label: "Recovery phone" }) || "";
}

/** API payload; an empty password is left out so the saved one is kept. */
export function socialAccountPayload(form) {
  const own = isOwnLogin(form);
  const twoFa = form.twoFaEnabled === "yes" ? true : form.twoFaEnabled === "no" ? false : null;
  const pageUrl = PLATFORMS_WITH_PAGE_URL.includes(form.platformName);
  return {
    Platform_Name: form.platformName,
    Account_Name: form.accountName.trim(),
    Login_Method: form.loginMethod || LOGIN_OWN,
    Linked_Account_Id: own || !form.linkedAccountId ? null : Number(form.linkedAccountId),
    Username: form.username.trim() || null,
    ...(own && form.password.trim() ? { Password: form.password.trim() } : {}),
    Profile_URL: form.profileUrl.trim() || null,
    Business_URL: pageUrl ? form.businessUrl.trim() || null : null,
    Email_Used: form.emailUsed.trim() || null,
    Recovery_Email: form.recoveryEmail.trim() || null,
    Contact_Number: form.contactNumber || null,
    Two_FA_Enabled: twoFa,
    Two_FA_Method: twoFa ? form.twoFaMethod || null : null,
    Two_FA_Holder: twoFa ? form.twoFaHolder.trim() || null : null,
    Access_Type: form.accessType || null,
    Account_Owner: form.accountOwner || null,
    Verification_Status: form.verificationStatus || null,
    client_id: form.clientId ? Number(form.clientId) : null,
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
  };
}

function Select({ label, name, value, onChange, options, placeholder = "Select", required = false, renderLabel }) {
  return (
    <div className="form-group">
      <label>
        {label} {required ? <span className="required">*</span> : null}
      </label>
      <select name={name} value={value} onChange={onChange} required={required}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {renderLabel ? renderLabel(o) : o}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Linked Gmail / Facebook accounts of the same client that this account can sign in with. */
function useLoginAccounts(clientId, loginMethod, accountId) {
  const [accounts, setAccounts] = useState([]);
  const platforms = LOGIN_PROVIDER_PLATFORMS[loginMethod];

  useEffect(() => {
    if (!clientId || !platforms) {
      setAccounts([]);
      return undefined;
    }
    let active = true;
    axiosInstance
      .get(`/social-accounts/client/${clientId}`)
      .then((res) => {
        if (!active) return;
        const rows = res.data?.accounts || [];
        setAccounts(
          rows.filter(
            (a) =>
              platforms.includes(String(a.Platform_Name || "").toLowerCase()) &&
              String(a.Account_Id) !== String(accountId || "")
          )
        );
      })
      .catch(() => active && setAccounts([]));
    return () => {
      active = false;
    };
  }, [clientId, platforms, accountId]);

  return accounts;
}

/**
 * Staff add / edit form body for a social account: basics, login, security and ownership.
 * ``hasSavedPassword``: editing an account whose password is already saved (blank keeps it).
 */
export default function SocialAccountFields({ form, onChange, hasSavedPassword = false, accountId = null }) {
  const own = isOwnLogin(form);
  const loginAccounts = useLoginAccounts(form.clientId, own ? null : form.loginMethod, accountId);
  const showPageUrl = PLATFORMS_WITH_PAGE_URL.includes(form.platformName);
  const twoFaOn = form.twoFaEnabled === "yes";
  const legacyPlatform = form.platformName && !SOCIAL_PLATFORM_VALUES.includes(form.platformName);

  return (
    <>
      <div className="form-section">
        <h3 className="section-title">Account</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>
              Platform <span className="required">*</span>
            </label>
            <select name="platformName" value={form.platformName} onChange={onChange} required>
              <option value="">Select platform</option>
              {SOCIAL_PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              {legacyPlatform ? (
                <option value={form.platformName}>{socialPlatformLabel(form.platformName)}</option>
              ) : null}
            </select>
          </div>
          <div className="form-group">
            <label>
              Account Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="accountName"
              value={form.accountName}
              onChange={onChange}
              placeholder="Page / channel / business name"
              required
            />
          </div>
          <Select
            label="Status"
            name="status"
            value={form.status}
            onChange={onChange}
            options={SOCIAL_ACCOUNT_STATUSES}
            placeholder="Select status"
          />
          <div className="form-group">
            <label>Profile URL</label>
            <input type="url" name="profileUrl" value={form.profileUrl} onChange={onChange} />
          </div>
          {showPageUrl ? (
            <div className="form-group">
              <label>{form.platformName === "gmb" ? "Google Maps Listing URL" : "Business Page URL"}</label>
              <input type="url" name="businessUrl" value={form.businessUrl} onChange={onChange} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="form-section edittenant-section-divider">
        <h3 className="section-title">Login</h3>
        <div className="form-grid">
          <Select
            label="Login Method"
            name="loginMethod"
            value={form.loginMethod}
            onChange={onChange}
            options={LOGIN_METHODS}
            required
          />
          {own ? (
            <>
              <div className="form-group">
                <label>
                  Username <span className="required">*</span>
                </label>
                <input type="text" name="username" value={form.username} onChange={onChange} required />
              </div>
              <div className="form-group">
                <label>
                  {hasSavedPassword ? "New Password" : "Password"}
                  {hasSavedPassword ? null : <span className="required"> *</span>}
                </label>
                <PasswordInput
                  name="password"
                  value={form.password}
                  onChange={onChange}
                  autoComplete="new-password"
                  placeholder={hasSavedPassword ? "Leave blank to keep the current password" : ""}
                  required={!hasSavedPassword}
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Signs in with</label>
                <select name="linkedAccountId" value={form.linkedAccountId} onChange={onChange}>
                  <option value="">
                    {loginAccounts.length ? "Select account" : "No matching account saved for this client"}
                  </option>
                  {loginAccounts.map((a) => (
                    <option key={a.Account_Id} value={a.Account_Id}>
                      {socialPlatformLabel(a.Platform_Name)} — {a.Email_Used || a.Username || a.Account_Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Handle / Username</label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={onChange}
                  placeholder="Optional, e.g. @acme"
                />
              </div>
            </>
          )}
          <div className="form-group">
            <label>Email Used</label>
            <input type="email" name="emailUsed" value={form.emailUsed} onChange={onChange} />
          </div>
        </div>
        {!own ? (
          <p className="edittenant-section-hint socialacc-hint">
            The password is kept on the linked account only, so it needs updating in one place.
          </p>
        ) : null}
      </div>

      <div className="form-section edittenant-section-divider">
        <h3 className="section-title">Security &amp; Recovery</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>2-Step Verification</label>
            <select name="twoFaEnabled" value={form.twoFaEnabled} onChange={onChange}>
              <option value="">Not known</option>
              <option value="yes">On</option>
              <option value="no">Off</option>
            </select>
          </div>
          {twoFaOn ? (
            <>
              <Select
                label="Code Sent By"
                name="twoFaMethod"
                value={form.twoFaMethod}
                onChange={onChange}
                options={TWO_FA_METHODS}
              />
              <div className="form-group">
                <label>Code Received By</label>
                <input
                  type="text"
                  name="twoFaHolder"
                  value={form.twoFaHolder}
                  onChange={onChange}
                  placeholder="Person / phone that gets the code"
                />
              </div>
            </>
          ) : null}
          <div className="form-group">
            <label>Recovery Email</label>
            <input type="email" name="recoveryEmail" value={form.recoveryEmail} onChange={onChange} />
          </div>
          <div className="form-group">
            <label>Recovery Phone</label>
            <input type="text" name="contactNumber" value={form.contactNumber} onChange={onChange} />
          </div>
        </div>
      </div>

      <div className="form-section edittenant-section-divider">
        <h3 className="section-title">Ownership</h3>
        <div className="form-grid">
          <Select
            label="Access Type"
            name="accessType"
            value={form.accessType}
            onChange={onChange}
            options={ACCESS_TYPES}
          />
          <Select
            label="Account Owner"
            name="accountOwner"
            value={form.accountOwner}
            onChange={onChange}
            options={ACCOUNT_OWNERS}
            renderLabel={(o) => (o === "Agency" ? "Agency (created by us)" : "Client")}
          />
          <Select
            label="Verification"
            name="verificationStatus"
            value={form.verificationStatus}
            onChange={onChange}
            options={VERIFICATION_STATUSES}
          />
          <div className="form-group socialacc-full">
            <label>Notes</label>
            <textarea name="notes" value={form.notes} onChange={onChange} rows="3" />
          </div>
        </div>
      </div>
    </>
  );
}
